import React, { useEffect, useState, useRef } from 'react';
import { Card, Row, Col, Typography, List, Button, Spin, Progress, Steps } from 'antd';
import { 
  PlayCircleOutlined,
  HistoryOutlined,
  LoadingOutlined,
  RocketOutlined,
  LineChartOutlined,
  DollarOutlined,
  TrophyOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import SpiderRadarChart from './SpiderRadarChart';
import MQTTService from '../../services/mqtt.service';
import { useDispatch } from 'react-redux';
import { setBacktestProgress, updateBacktestResult } from '../../store/slices/strategySlice';
import TradingService from '../../services/trading.service';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useParams } from 'react-router-dom';
import { Form } from 'antd';
import BacktestModal from './modals/BacktestModal';
import TradeHistoryModal from './modals/TradeHistoryModal';
import DeployModal from './modals/DeployModal';
import ChartModal from './modals/ChartModal';

const { Text, Title } = Typography;
const { Step } = Steps;

interface Trade {
  date: string;
  type: 'buy' | 'sell';
  profit: number;
}

interface BacktestResultsProps {
  jobId?: number;
  isLoading: boolean;
  progress?: number;
  onBacktest: (startDate: string, endDate: string, advancedSettings?: any) => void;
  onDeploy?: () => void;
  results?: {
    profitLoss?: number;
    winRate?: number;
    totalTrades?: number;
    averageProfit?: number;
    averageLoss?: number;
    maxDrawdown?: number;
    duration?: string;
    trades?: Trade[];
    chartData?: any[];
  };
}

const BacktestResults: React.FC<BacktestResultsProps> = ({ 
  jobId,
  isLoading,
  progress = 0,
  onBacktest,
  results = {} 
}) => {
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [isDeployModalVisible, setIsDeployModalVisible] = useState(false);
  const [isBotCreated, setIsBotCreated] = useState(false);
  const [isChartModalVisible, setIsChartModalVisible] = useState(false);
  const [isBacktestModalVisible, setIsBacktestModalVisible] = useState(false);
  const [backtestProgress, setBacktestProgressState] = useState(progress);
  const [currentStep, setCurrentStep] = useState(0);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('3m');
  
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [backtestForm] = Form.useForm();
  const { id: strategyId } = useParams<{ id: string }>();
  const strategyName = useAppSelector(state => state.strategy.strategyName);
  const mqttService = useRef(MQTTService.getInstance());

  useEffect(() => {
    if (jobId && isLoading) {
      const currentMqttService = mqttService.current;
      const handleBacktestUpdate = (data: any) => {
        if (data.progress !== undefined) {
          setBacktestProgressState(data.progress);
          dispatch(setBacktestProgress(data.progress));
        }
        if (data.status === 'success' && data.result) {
          dispatch(updateBacktestResult(data.result));
        }

        const statusSteps = ['startup', 'dataload', 'analyze', 'convert', 'backtest', 'success'];
        const stepIndex = statusSteps.indexOf(data.status);
        if (stepIndex !== -1) {
          setCurrentStep(stepIndex);
        }
      };

      currentMqttService.subscribeToBacktest(jobId, handleBacktestUpdate);

      return () => {
        currentMqttService.unsubscribeFromBacktest(jobId, handleBacktestUpdate);
      };
    }
  }, [jobId, isLoading, dispatch]);

  useEffect(() => {
    setBacktestProgressState(progress);
  }, [progress]);

  const handleCreateBot = async (values: any) => {
    try {
      const response = await TradingService.createBot(values);
      if (response.success) {
        setIsBotCreated(true);
        form.resetFields();
      }
    } catch (error) {
      console.error('Error creating bot:', error);
    }
  };

  const handleBacktestSubmit = (values: any) => {
    onBacktest(
      values.startDate,
      values.endDate,
      isAdvancedMode ? {
        leverage: values.leverage,
        takeProfit: values.takeProfit,
        stopLoss: values.stopLoss
      } : undefined
    );
    setIsBacktestModalVisible(false);
  };

  const showBacktestModal = () => {
    setIsBacktestModalVisible(true);
  };

  const getProfitLossColor = (value: number) => {
    if (value > 0) return '#52c41a';
    if (value < 0) return '#ff4d4f';
    return '#faad14';
  };

  return (
    <Card
      className="strategy-card"
      title={
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Title level={4} style={{ margin: 0, fontSize: '16px' }}>
            Kiểm thử chiến lược
          </Title>
          <button
            type="button"
            onClick={showBacktestModal}
            disabled={isLoading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px 15px',
              borderRadius: '6px',
              border: 'none',
              background: '#1677ff',
              color: 'white',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? <LoadingOutlined spin /> : <PlayCircleOutlined />}
            {isLoading ? 'Đang xử lý...' : 'Backtest'}
          </button>
        </div>
      }
      style={{ borderRadius: '16px' }}
    >
      {isLoading ? (
        <div style={{ 
          padding: '40px 0', 
          textAlign: 'center',
          background: 'rgba(0,0,0,0.02)',
          borderRadius: '12px',
          margin: '8px 0'
        }}>
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} 
            tip="Đang phân tích chiến lược..."
          />
          {currentStep === 4 && (
            <div style={{ width: '80%', margin: '20px auto 0' }}>
              <Progress 
                percent={backtestProgress} 
                status="active"
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Đã hoàn thành {backtestProgress}% quá trình phân tích
              </Text>
            </div>
          )}
          <Steps current={currentStep} size="small" style={{ marginTop: '20px', fontSize: '12px', padding: '0 10px' }}>
            <Step title="Khởi động" />
            <Step title="Tải Data" />
            <Step title="Phân tích" />
            <Step title="Convert" />
            <Step title="Backtest" />
            <Step title="Hoàn thành" />
          </Steps>
        </div>
      ) : (
        <>
          <div style={{ 
            marginTop: '16px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px'
          }}>
            <Button 
              type="default"
              icon={<HistoryOutlined />}
              onClick={() => setIsHistoryVisible(true)}
              style={{ borderRadius: '6px' }}
            >
              Xem lịch sử giao dịch
            </Button>
            <Button 
              type="default"
              icon={<LineChartOutlined />}
              onClick={() => setIsChartModalVisible(true)}
              style={{ borderRadius: '6px' }}
            >
              Xem chart
            </Button>
          </div>
          <Row gutter={[8, 8]}>
            <Col xs={24} sm={8}>
              <div style={{ fontFamily: 'Courier New, monospace', fontSize: '12px', padding: '10px' }}>
                <List
                  itemLayout="horizontal"
                  dataSource={[
                    { label: 'Lợi nhuận/Lỗ', value: results.profitLoss !== undefined ? results.profitLoss : 0, icon: <DollarOutlined />, isProfit: true },
                    { label: 'Tỷ lệ thắng', value: `${results.winRate !== undefined ? results.winRate : 0}%`, icon: <TrophyOutlined /> },
                    { label: 'Tổng số giao dịch', value: results.totalTrades !== undefined ? results.totalTrades : 0, icon: <BarChartOutlined /> },
                    { label: 'Lợi nhuận trung bình', value: results.averageProfit !== undefined ? results.averageProfit : 0, icon: <BarChartOutlined /> },
                    { label: 'Lỗ trung bình', value: results.averageLoss !== undefined ? results.averageLoss : 0, icon: <BarChartOutlined /> },
                    { label: 'Giảm tối đa', value: results.maxDrawdown !== undefined ? results.maxDrawdown : 0, icon: <LineChartOutlined /> },
                    { label: 'Thời gian', value: results.duration !== undefined ? results.duration : 'N/A', icon: <ClockCircleOutlined /> },
                  ]}
                  renderItem={item => (
                    <List.Item style={{ border: 'none', display: 'flex', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {item.icon}
                        <Text strong style={{ fontSize: '14px' }}>{item.label}:</Text>
                      </div>
                      <Text style={{ 
                        fontSize: '12px',
                        color: item.isProfit ? getProfitLossColor(results.profitLoss || 0) : undefined
                      }}>
                        {item.value}
                      </Text>
                    </List.Item>
                  )}
                />
              </div>
            </Col>
            <Col xs={24} sm={16}>
              <Card size="small" style={{ borderRadius: '12px', border: 'none', height: '300px' }}>
                <SpiderRadarChart />
              </Card>
            </Col>
          </Row>

          <div style={{
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <Button
              type="primary"
              size="large"
              icon={<RocketOutlined />}
              onClick={() => setIsDeployModalVisible(true)}
              style={{ 
                borderRadius: '8px',
                height: '48px',
                fontSize: '16px',
                padding: '0 32px',
                background: '#52c41a',
                borderColor: '#52c41a'
              }}
            >
              Triển khai chiến lược
            </Button>
          </div>

          <BacktestModal
            isVisible={isBacktestModalVisible}
            onClose={() => setIsBacktestModalVisible(false)}
            onSubmit={handleBacktestSubmit}
            isAdvancedMode={isAdvancedMode}
            setIsAdvancedMode={setIsAdvancedMode}
            selectedTimeRange={selectedTimeRange}
            setSelectedTimeRange={setSelectedTimeRange}
            form={backtestForm}
          />

          <TradeHistoryModal
            isVisible={isHistoryVisible}
            onClose={() => setIsHistoryVisible(false)}
            trades={results.trades}
          />

          <DeployModal
            isVisible={isDeployModalVisible}
            onClose={() => setIsDeployModalVisible(false)}
            onSubmit={handleCreateBot}
            form={form}
            isBotCreated={isBotCreated}
            strategyId={strategyId}
            strategyName={strategyName}
          />

          <ChartModal
            isVisible={isChartModalVisible}
            onClose={() => setIsChartModalVisible(false)}
            chartData={results.chartData}
          />
        </>
      )}
    </Card>
  );
};

export default BacktestResults;
